/**
 * TemplateListItem Component
 *
 * Individual template item in the sidebar list.
 * Displays template name, ownership indicator, and pin/drag controls.
 * Pinned templates are sortable via drag-and-drop.
 */

import React, { useCallback, useRef, useState } from 'react';
import Logo from '@components/Icons/Logo';
import { DragHandle } from '@components/TemplateSections';
import { useThemeContext } from '@context/theme/context';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AccountCircle, Groups, PushPin, PushPinOutlined } from '@mui/icons-material';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { TemplateWithMeta } from '../types';

interface TemplateListItemProps {
  /** Template data with metadata */
  template: TemplateWithMeta;
  /** Whether this template is selected */
  isSelected: boolean;
  /** Click handler */
  onClick: () => void;
  /** Pin/unpin click handler */
  onPinClick?: (e: React.MouseEvent) => void;
  /** Whether this template is pinned */
  isPinned: boolean;
}

export const TemplateListItem: React.FC<TemplateListItemProps> = ({
  template,
  isSelected,
  onClick,
  onPinClick,
  isPinned,
}) => {
  const theme = useTheme();
  const { brandLanguage } = useThemeContext();
  const textRef = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const handleMouseEnter = useCallback(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, []);

  // Make pinned templates sortable
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: template.template.id,
    disabled: !isPinned,
    animateLayoutChanges: () => false,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Get the appropriate icon and tooltip based on owner_scope
  const getTemplateIcon = () => {
    switch (template.template.owner_scope) {
      case 'user':
        return (
          <Tooltip
            title="Your template - fully customizable"
            placement="top"
            arrow
          >
            <AccountCircle
              sx={{
                fontSize: 20,
                color: isSelected ? 'primary.main' : 'text.secondary',
              }}
            />
          </Tooltip>
        );
      case 'org':
        return (
          <Tooltip
            title={
              template.permissions.can_edit
                ? 'Organization template - you can edit'
                : 'Organization template - only admins can edit'
            }
            placement="top"
            arrow
          >
            <Groups
              sx={{
                fontSize: 20,
                color: isSelected ? 'primary.main' : 'text.secondary',
              }}
            />
          </Tooltip>
        );
      case 'public':
        return (
          <Tooltip
            title="Customizing will create a copy"
            placement="top"
            arrow
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Logo size={18} />
            </Box>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1.5,
        borderRadius: 2,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        bgcolor: isSelected ? 'action.selected' : 'background.paper',
        transition: theme.transitions.create(
          ['border-color', 'background-color', 'box-shadow'],
          { duration: theme.transitions.duration.short },
        ),
        '&:hover': {
          bgcolor: isSelected ? 'action.selected' : 'action.hover',
          borderColor: isSelected ? 'primary.main' : 'primary.light',
        },
      }}
    >
      {/* Drag Handle - only for pinned templates */}
      {isPinned && (
        <DragHandle
          listeners={listeners}
          attributes={attributes}
          disabled={false}
          size="small"
        />
      )}

      {/* Template Name */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Tooltip
          title={isTruncated ? template.displayName : ''}
          placement="top"
          arrow
          enterDelay={300}
        >
          <Typography
            ref={textRef}
            variant="body2"
            onMouseEnter={handleMouseEnter}
            sx={{fontWeight: isSelected ? 600 : 500,
              color: isSelected ? 'primary.main' : 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {template.displayName}
          </Typography>
        </Tooltip>
        {/* Subtle read-only indicator for locked org templates */}
        {template.template.owner_scope === 'org' && template.isLocked && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              fontSize: '0.7rem',
              lineHeight: 1,
              mt: 0.25,
            }}
          >
            Read-only
          </Typography>
        )}
      </Box>

      {/* Template Type Icon */}
      {getTemplateIcon()}

      {/* Pin Icon */}
      {onPinClick && (
        <Tooltip
          title={isPinned ? 'Unpin template' : 'Pin template'}
          placement="top"
          arrow
        >
          <IconButton
            size="small"
            onClick={onPinClick}
            sx={{
              color: isPinned
                ? isSelected
                  ? 'primary.main'
                  : 'primary.light'
                : 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
            aria-label={isPinned ? 'Unpin template' : 'Pin template'}
          >
            {isPinned ? (
              <PushPin sx={{ fontSize: 18 }} />
            ) : (
              <PushPinOutlined sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
